import { Component, computed, ElementRef, inject, OnInit, signal, ViewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  IonBackButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonMenuButton,
  IonTitle,
  IonToolbar,
  NavController,
} from '@ionic/angular/standalone';
import { TranslatePipe } from '@ngx-translate/core';
import { lastValueFrom } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import {
  WorkDocument,
  WorkDocumentsGeneratorV3Service,
  WorkDocumentsV2Service,
  WorkDocxOutputFormatEnum,
  WorkProject,
  WorkProjectsV2Service,
  WorkTask,
  WorkTasksV2Service,
} from '@work-frontend-monorepo/work-rest-api';
import dayjs from 'dayjs';
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Store } from '@ngxs/store';
import { LoadingAndFeedbackState } from '@work-frontend-monorepo/work-ui/utilities';
import { BadgeComponent, ButtonComponent } from '@work-frontend-monorepo/work-ui/layout';
import { GetFullnameOfContactPipe, GetLabelVariantForStatusPipe, PrependServerToFileSrcPipe } from '@work-frontend-monorepo/work-ui/pipes';
import { CheckboxComponent, InputFieldComponent } from '@work-frontend-monorepo/work-ui/forms';
import { ExceptionHandlingService } from '@work-frontend-monorepo/work-ui/services';
import { ProfileState } from '@work-frontend-monorepo/work-ui/state-management';
import { GalleryComponent } from '@work-frontend-monorepo/work-ui/rich-content';
import { FormWizardComponent, FormWizardStepDirective } from '@work-frontend-monorepo/work-ui/wizard';
import { NgClass } from '@angular/common';

interface AbnahmeQuestion {
  key: string;
  question: string;
  notes?: string[];
  answers: string[];
  images?: number;
  step: number;
}

interface AbschlussprotokollForm {
  // Existing fields
  geruestTyp: FormControl<{ [key: string]: boolean }>;
  lastklasse: FormControl<number | null>;
  anleitungAmGeruest: FormControl<boolean>;
  anleitungAmErsteller: FormControl<boolean>;
  anleitungFreitext: FormControl<string>;
  emailContacts: FormControl<{ [key: string]: boolean }>;
  signature: FormControl<string | null>;
  // Abnahme question fields
  q1_tragfaehigkeit: FormControl<string | null>;
  q2_zugaenge: FormControl<string | null>;
  q3_unterlage: FormControl<string | null>;
  q4_verankerungen_anzahl: FormControl<string | null>;
  q5_verankerungen_festigkeit: FormControl<string | null>;
  q6_geruest_belaege: FormControl<string | null>;
  q7_seitenschutz_aussen: FormControl<string | null>;
  q8_seitenschutz_innen: FormControl<string | null>;
  q9_spenglergang_belag: FormControl<string | null>;
  q10_spenglergang_abmessungen: FormControl<string | null>;
  q11_dachdeckerschutzwand: FormControl<string | null>;
  q12_giebel_absturz: FormControl<string | null>;
  q13_drittpersonen: FormControl<string | null>;
  q14_gefahrenstellen: FormControl<string | null>;
}

@Component({
  selector: 'sfg-app-projekte-details-abschlussprotokoll-page',
  templateUrl: './projekte-details-abschlussprotokoll-page.component.html',
  styleUrls: ['./projekte-details-abschlussprotokoll-page.component.scss'],
  standalone: true,
  imports: [
    IonContent,
    IonHeader,
    IonTitle,
    IonToolbar,
    IonMenuButton,
    IonButtons,
    TranslatePipe,
    IonBackButton,
    ButtonComponent,
    BadgeComponent,
    GetLabelVariantForStatusPipe,
    CheckboxComponent,
    InputFieldComponent,
    GalleryComponent,
    ReactiveFormsModule,
    NgClass,
    FormWizardComponent,
    FormWizardStepDirective,
    GetFullnameOfContactPipe,
  ],
  providers: [GetFullnameOfContactPipe],
})
export class ProjekteDetailsAbschlussprotokollPageComponent implements OnInit {
  @ViewChild('signatureCanvas', { static: false }) signatureCanvas!: ElementRef<HTMLCanvasElement>;

  project = signal<WorkProject | undefined>(undefined);
  task = signal<WorkTask | undefined>(undefined);
  documents = signal<WorkDocument[]>([]);
  isDrawing = false;
  isCompleting = false;
  uploadLoadingState = signal<LoadingAndFeedbackState>('none');
  isUploading = signal<boolean>(false);
  completeDisabled = computed(() => this.isUploading() || this.documents().length === 0);

  private readonly exceptionHandlingService = inject(ExceptionHandlingService);
  private readonly workProjectsV2Service = inject(WorkProjectsV2Service);
  private readonly workTasksV2Service = inject(WorkTasksV2Service);
  private readonly workDocumentsV2Service = inject(WorkDocumentsV2Service);
  private readonly workDocumentsGeneratorV3Service = inject(WorkDocumentsGeneratorV3Service);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly navController = inject(NavController);
  private readonly store = inject(Store);
  private readonly prependServerToFileSrcPipe = inject(PrependServerToFileSrcPipe);

  private canvasContext: CanvasRenderingContext2D | null = null;

  protected profile = toSignal(this.store.select(ProfileState.profile));

  protected geruestTypOptions = [
    { key: 'mato1', label: 'Mato 1' },
    { key: 'mato8', label: 'Mato 8' },
  ];

  protected readonly questions: AbnahmeQuestion[] = [
    // Step 1: Tragfähigkeit & Zugänge
    {
      key: 'q1_tragfaehigkeit',
      question: 'ABNAHME_Q1_TRAGFAEHIGKEIT',
      notes: ['ABNAHME_Q1_HINWEIS_MALERGERUEST', 'ABNAHME_Q1_HINWEIS_MAURERGERUEST', 'ABNAHME_Q1_HINWEIS_STEINHAUERGERUEST'],
      answers: ['ja', 'nein'],
      step: 1,
    },
    {
      key: 'q2_zugaenge',
      question: 'ABNAHME_Q2_ZUGAENGE',
      notes: ['ABNAHME_Q2_HINWEIS_DISTANZ', 'ABNAHME_Q2_HINWEIS_NUTZLAST'],
      answers: ['ja', 'nein'],
      images: 1,
      step: 1,
    },
    // Step 2: Unterlage & Verankerungen
    {
      key: 'q3_unterlage',
      question: 'ABNAHME_Q3_UNTERLAGE',
      answers: ['ja', 'nein', 'teilweise'],
      step: 2,
    },
    {
      key: 'q4_verankerungen_anzahl',
      question: 'ABNAHME_Q4_VERANKERUNGEN_ANZAHL',
      notes: ['ABNAHME_Q4_HINWEIS_UNVERKLEIDET', 'ABNAHME_Q4_HINWEIS_NETZE'],
      answers: ['ja', 'nein'],
      step: 2,
    },
    {
      key: 'q5_verankerungen_festigkeit',
      question: 'ABNAHME_Q5_VERANKERUNGEN_FESTIGKEIT',
      answers: ['ja', 'nein', 'teilweise'],
      images: 1,
      step: 2,
    },
    // Step 3: Beläge & Seitenschutz
    {
      key: 'q6_geruest_belaege',
      question: 'ABNAHME_Q6_GERUEST_BELAEGE',
      notes: ['ABNAHME_Q6_HINWEIS_NUTZLAST', 'ABNAHME_Q6_HINWEIS_SCHALTAFELN'],
      answers: ['ja', 'nein', 'teilweise'],
      step: 3,
    },
    {
      key: 'q7_seitenschutz_aussen',
      question: 'ABNAHME_Q7_SEITENSCHUTZ_AUSSEN',
      notes: ['ABNAHME_Q7_HINWEIS_SEITENSCHUTZ'],
      answers: ['ja', 'nein'],
      step: 3,
    },
    {
      key: 'q8_seitenschutz_innen',
      question: 'ABNAHME_Q8_SEITENSCHUTZ_INNEN',
      notes: ['ABNAHME_Q8_HINWEIS_ZWEITEILIG'],
      answers: ['ja', 'nein'],
      step: 3,
    },
    // Step 4: Spenglergang
    {
      key: 'q9_spenglergang_belag',
      question: 'ABNAHME_Q9_SPENGLERGANG_BELAG',
      notes: ['ABNAHME_Q9_HINWEIS_STAHLBELAG'],
      answers: ['ja', 'nein', 'teilweise'],
      step: 4,
    },
    {
      key: 'q10_spenglergang_abmessungen',
      question: 'ABNAHME_Q10_SPENGLERGANG_ABMESSUNGEN',
      answers: ['ja', 'nein', 'teilweise'],
      images: 2,
      step: 4,
    },
    // Step 5: Dachränder
    {
      key: 'q11_dachdeckerschutzwand',
      question: 'ABNAHME_Q11_DACHDECKERSCHUTZWAND',
      answers: ['ja', 'nein'],
      images: 2,
      step: 5,
    },
    {
      key: 'q12_giebel_absturz',
      question: 'ABNAHME_Q12_GIEBEL_ABSTURZ',
      answers: ['ja', 'nein'],
      step: 5,
    },
    // Step 6: Verkehrssicherheit
    {
      key: 'q13_drittpersonen',
      question: 'ABNAHME_Q13_DRITTPERSONEN',
      answers: ['ja', 'nein'],
      step: 6,
    },
    {
      key: 'q14_gefahrenstellen',
      question: 'ABNAHME_Q14_GEFAHRENSTELLEN',
      answers: ['ja', 'nein'],
      step: 6,
    },
  ];

  protected form = new FormGroup<AbschlussprotokollForm>({
    geruestTyp: new FormControl<{ [key: string]: boolean }>(
      { mato1: false, mato8: false },
      {
        nonNullable: true,
        validators: [
          (control: AbstractControl<{ [key: string]: boolean }>) => {
            const value = control.value;
            const hasSelection = Object.values(value).some((selected) => selected);
            return hasSelection ? null : { required: true };
          },
        ],
      },
    ),
    lastklasse: new FormControl<number | null>(null, [Validators.required, Validators.min(0)]),
    anleitungAmGeruest: new FormControl(false, { nonNullable: true }),
    anleitungAmErsteller: new FormControl(false, { nonNullable: true }),
    anleitungFreitext: new FormControl('', { nonNullable: true }),
    emailContacts: new FormControl<{ [key: string]: boolean }>({}, { nonNullable: true }),
    signature: new FormControl<string | null>(null, Validators.required),
    q1_tragfaehigkeit: new FormControl<string | null>(null),
    q2_zugaenge: new FormControl<string | null>(null),
    q3_unterlage: new FormControl<string | null>(null),
    q4_verankerungen_anzahl: new FormControl<string | null>(null),
    q5_verankerungen_festigkeit: new FormControl<string | null>(null),
    q6_geruest_belaege: new FormControl<string | null>(null),
    q7_seitenschutz_aussen: new FormControl<string | null>(null),
    q8_seitenschutz_innen: new FormControl<string | null>(null),
    q9_spenglergang_belag: new FormControl<string | null>(null),
    q10_spenglergang_abmessungen: new FormControl<string | null>(null),
    q11_dachdeckerschutzwand: new FormControl<string | null>(null),
    q12_giebel_absturz: new FormControl<string | null>(null),
    q13_drittpersonen: new FormControl<string | null>(null),
    q14_gefahrenstellen: new FormControl<string | null>(null),
  });

  async ngOnInit() {
    const projectId = this.activatedRoute.snapshot.params['projectId'];
    if (projectId) {
      await this.loadProjectDetails(projectId);
      await this.loadTask(projectId);
      await this.loadDocuments(projectId);
    }
  }

  // ─── Canvas ───────────────────────────────────────────────────────────────

  protected onSignatureStepActive(active: boolean): void {
    if (active) {
      setTimeout(() => this.initializeCanvas(), 100);
    }
  }

  protected initializeCanvas() {
    if (this.signatureCanvas) {
      const canvas = this.signatureCanvas.nativeElement;
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width;
      canvas.height = rect.height;

      this.canvasContext = canvas.getContext('2d');
      if (this.canvasContext) {
        this.canvasContext.fillStyle = '#FFFFFF';
        this.canvasContext.fillRect(0, 0, canvas.width, canvas.height);
        this.canvasContext.strokeStyle = '#000000';
        this.canvasContext.lineWidth = 3;
        this.canvasContext.lineCap = 'round';
        this.canvasContext.lineJoin = 'round';
      }
    }
  }

  protected startDrawing(event: TouchEvent | MouseEvent) {
    this.isDrawing = true;
    const canvas = this.signatureCanvas.nativeElement;
    const rect = canvas.getBoundingClientRect();

    let x: number, y: number;
    if (event instanceof TouchEvent) {
      x = event.touches[0].clientX - rect.left;
      y = event.touches[0].clientY - rect.top;
    } else {
      x = event.clientX - rect.left;
      y = event.clientY - rect.top;
    }

    if (this.canvasContext) {
      this.canvasContext.beginPath();
      this.canvasContext.moveTo(x, y);
    }
  }

  protected draw(event: TouchEvent | MouseEvent) {
    if (!this.isDrawing) return;

    const canvas = this.signatureCanvas.nativeElement;
    const rect = canvas.getBoundingClientRect();

    let x: number, y: number;
    if (event instanceof TouchEvent) {
      event.preventDefault();
      x = event.touches[0].clientX - rect.left;
      y = event.touches[0].clientY - rect.top;
    } else {
      x = event.clientX - rect.left;
      y = event.clientY - rect.top;
    }

    if (this.canvasContext) {
      this.canvasContext.lineTo(x, y);
      this.canvasContext.stroke();
      this.form.controls.signature.setValue('signed');
    }
  }

  protected stopDrawing() {
    this.isDrawing = false;
  }

  protected clearSignature() {
    if (this.canvasContext && this.signatureCanvas) {
      const canvas = this.signatureCanvas.nativeElement;
      this.canvasContext.fillStyle = '#FFFFFF';
      this.canvasContext.fillRect(0, 0, canvas.width, canvas.height);
      this.form.controls.signature.setValue(null);
    }
  }

  // ─── Gerüsttyp & Lastklasse ───────────────────────────────────────────────

  protected setLastklasse(value: number) {
    this.form.controls.lastklasse.setValue(value);
  }

  protected toggleGeruestTyp(key: string) {
    const currentValue = this.form.controls.geruestTyp.value;
    this.form.controls.geruestTyp.setValue({
      ...currentValue,
      [key]: !currentValue[key],
    });
  }

  protected getSelectedGeruestTypen(): string[] {
    const geruestTypValue = this.form.controls.geruestTyp.value;
    return Object.entries(geruestTypValue)
      .filter(([_, selected]) => selected)
      .map(([key]) => {
        const option = this.geruestTypOptions.find((opt) => opt.key === key);
        return option?.label || key;
      });
  }

  // ─── Abnahme question helpers ─────────────────────────────────────────────

  protected getQuestionsForStep(step: number): AbnahmeQuestion[] {
    return this.questions.filter((q) => q.step === step);
  }

  protected setAnswer(controlName: string, value: string) {
    this.form.get(controlName)?.setValue(value);
  }

  protected getAnswer(controlName: string): string | null {
    return this.form.get(controlName)?.value ?? null;
  }

  protected getAnswerLabel(answer: string): string {
    const labels: Record<string, string> = { ja: 'JA', nein: 'NEIN', teilweise: 'TEILWEISE' };
    return labels[answer] ?? answer;
  }

  protected asArray(n: number): unknown[] {
    return Array.from({ length: n });
  }

  // ─── Documents ────────────────────────────────────────────────────────────

  protected onDocumentsUploaded(documents: WorkDocument[]) {
    this.documents.set([...this.documents(), ...documents]);
  }

  protected onDocumentDeleted(documents: WorkDocument[]) {
    const deletedIds = documents.map((d) => d.id);
    this.documents.set(this.documents().filter((d) => !deletedIds.includes(d.id)));
  }

  // ─── Submit ───────────────────────────────────────────────────────────────

  protected async completeTask() {
    if (this.isCompleting || this.form.invalid) return;

    const currentTask = this.task();
    if (!currentTask) return;

    this.isCompleting = true;
    this.uploadLoadingState.set('loading');

    try {
      const now = dayjs().toISOString();

      await lastValueFrom(
        this.workTasksV2Service.tasksV2ControllerUpdate({
          id: currentTask.id,
          body: {
            active: currentTask.active ?? true,
            mandatory: currentTask.mandatory ?? false,
            name: currentTask.name ?? '',
            tasktypeId: currentTask.tasktypeId ?? 0,
            userId: this.profile()?.id ?? currentTask.userId ?? null,
            start: currentTask.start ?? now,
            end: now,
            status: 'Erledigt',
          },
        }),
      );

      const anleitungParts: string[] = [];
      if (this.form.value.anleitungAmGeruest) anleitungParts.push('Am Gerüst');
      if (this.form.value.anleitungAmErsteller) anleitungParts.push('Am Ersteller');
      if (this.form.value.anleitungFreitext && this.form.value.anleitungFreitext.trim()) {
        anleitungParts.push(this.form.value.anleitungFreitext.trim());
      }
      const anleitungString = anleitungParts.join(' / ');

      const imageUrls = this.documents()
        .filter((doc) => doc.fileSrc)
        .map((doc) => (doc.fileSrcOptimized ? doc.fileSrcOptimized : doc.fileSrc))
        .filter((url): url is string => !!url)
        .map((url) => this.prependServerToFileSrcPipe.transform(url));

      const selectedGeruestTypen = this.getSelectedGeruestTypen();

      let signatureBase64 = '';
      if (this.signatureCanvas) {
        signatureBase64 = this.signatureCanvas.nativeElement.toDataURL('image/png');
      }

      // Sammle mailIds aus den ausgewählten Contact-Emails
      const selectedMailIds: number[] = [];
      const externalProjectManagerMails = this.project()?.externalProjectManagerContact?.mails;
      if (externalProjectManagerMails && externalProjectManagerMails.length > 0) {
        externalProjectManagerMails.forEach((mail) => {
          selectedMailIds.push(mail.id);
        });
      }
      const clientContactMails = this.project()?.clientContact?.mails;
      if (clientContactMails && clientContactMails.length > 0) {
        clientContactMails.forEach((mail) => {
          if (!selectedMailIds.includes(mail.id)) {
            selectedMailIds.push(mail.id);
          }
        });
      }

      await lastValueFrom(
        this.workDocumentsGeneratorV3Service.documentsGeneratorV3ControllerGenerateFromTemplate({
          format: WorkDocxOutputFormatEnum.Pdf,
          body: {
            fileName: 'FREIGABE_GERUEST.docx',
            customData: {
              hersteller: selectedGeruestTypen,
              lastklasse: this.form.value.lastklasse,
              anleitung: anleitungString,
              datum: dayjs().format('DD.MM.YYYY'),
              imageUrls: imageUrls,
              signatureUrl: signatureBase64,
              q1_tragfaehigkeit: this.form.value.q1_tragfaehigkeit,
              q2_zugaenge: this.form.value.q2_zugaenge,
              q3_unterlage: this.form.value.q3_unterlage,
              q4_verankerungen_anzahl: this.form.value.q4_verankerungen_anzahl,
              q5_verankerungen_festigkeit: this.form.value.q5_verankerungen_festigkeit,
              q6_geruest_belaege: this.form.value.q6_geruest_belaege,
              q7_seitenschutz_aussen: this.form.value.q7_seitenschutz_aussen,
              q8_seitenschutz_innen: this.form.value.q8_seitenschutz_innen,
              q9_spenglergang_belag: this.form.value.q9_spenglergang_belag,
              q10_spenglergang_abmessungen: this.form.value.q10_spenglergang_abmessungen,
              q11_dachdeckerschutzwand: this.form.value.q11_dachdeckerschutzwand,
              q12_giebel_absturz: this.form.value.q12_giebel_absturz,
              q13_drittpersonen: this.form.value.q13_drittpersonen,
              q14_gefahrenstellen: this.form.value.q14_gefahrenstellen,
            },
            documenttypeId: 15,
            projectId: this.project()?.id,
            withBackground: true,
            saveAsDocument: true,
            mailIds: selectedMailIds,
          },
        }),
      );

      this.uploadLoadingState.set('success');

      await this.navController.navigateBack(['/mitarbeitende/projekte/details', this.project()?.id], {
        animated: true,
        animationDirection: 'back',
      });
    } catch (completeTaskException) {
      this.uploadLoadingState.set('error');
      this.exceptionHandlingService.handleExceptionWithAlert('completeTaskException', completeTaskException);
    } finally {
      this.uploadLoadingState.set('none');
      this.isCompleting = false;
    }
  }

  // ─── Data loading ─────────────────────────────────────────────────────────

  private async loadProjectDetails(projectId: number) {
    try {
      const projectResponse = await lastValueFrom(
        this.workProjectsV2Service.projectsV2ControllerFindOneById({
          id: projectId,
          withAllDetails: true,
        }),
      );
      this.project.set(projectResponse);

      if (projectResponse.contact?.mails) {
        const emailControls: { [key: string]: boolean } = {};
        projectResponse.contact.mails.forEach((mail) => {
          emailControls[mail.value] = false;
        });
        this.form.controls.emailContacts.setValue(emailControls);
      }
    } catch (loadProjectException) {
      this.exceptionHandlingService.handleExceptionWithAlert('loadProjectException', loadProjectException);
    }
  }

  private async loadDocuments(projectId: number) {
    try {
      const documentsResponse = await lastValueFrom(
        this.workDocumentsV2Service.documentsV2ControllerFindAllPaginated({
          'filter.projectId': [`$eq:${projectId}`],
          'filter.documentsubtypeId': ['$eq:11'],
          'filter.active': ['$eq:1'],
          limit: 100,
        }),
      );
      this.documents.set(documentsResponse.data ?? []);
    } catch (loadDocumentsException) {
      this.exceptionHandlingService.handleExceptionWithAlert('loadDocumentsException', loadDocumentsException);
    }
  }

  private async loadTask(projectId: number) {
    try {
      const standardTaskId = 6;

      const tasksResponse = await lastValueFrom(
        this.workTasksV2Service.tasksV2ControllerFindAllTasksForProjectsAndStandardtasks({
          body: {
            projectIds: [projectId],
            standardTaskIds: [standardTaskId],
          },
        }),
      );

      if (tasksResponse.length > 0) {
        this.task.set(tasksResponse[0]);
      }
    } catch (loadTaskException) {
      this.exceptionHandlingService.handleExceptionWithAlert('loadTaskException', loadTaskException);
    }
  }
}
